import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch } from '@fortawesome/free-solid-svg-icons';
import DataTable from './shared/DataTable';
import { formatDistanceToNow } from 'date-fns';

interface Comment {
  id: number;
  body: string;
  user: {
    name: string;
  };
  created_at: string;
  organization?: {
    name: string;
    id: number;
  };
  commentable_type?: string;
  commentable_id?: number;
}

interface PaginationMeta {
  current_page: number;
  total_pages: number;
  total_count: number;
}

interface Column {
  key: string;
  header: string;
  render?: (value: any, row?: any) => React.ReactNode;
}

const AllComments: React.FC = () => {
  const navigate = useNavigate();
  const [comments, setComments] = useState<Comment[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [meta, setMeta] = useState<PaginationMeta>({ current_page: 1, total_pages: 1, total_count: 0 });

  const fetchComments = async (page: number, search?: string) => {
    try {
      const searchParam = search ? `&search=${encodeURIComponent(search)}` : '';
      const response = await fetch(`/api/comments?page=${page}${searchParam}`);
      const data = await response.json();
      setComments(data);
      setMeta(data.meta);
    } catch (error) {
      console.error('Error fetching comments:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      fetchComments(1, searchTerm);
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchTerm]);

  const handlePageChange = (page: number) => {
    fetchComments(page, searchTerm);
  };

  const handleRowClick = (comment: Comment) => {
    if (comment.commentable_type === 'UsaSpendingRecipient' && comment.commentable_id) {
      navigate(`/organizations/${comment.commentable_id}`);
    }
  };

  const columns: Column[] = [
    {
      key: 'organization',
      header: 'Organization',
      render: (value: any) => value?.name || 'N/A',
    },
    {
      key: 'user',
      header: 'Author',
      render: (value: any) => value?.name || 'N/A',
    },
    {
      key: 'body',
      header: 'Comment',
      render: (value: string) => <div className="max-w-xl truncate">{value}</div>,
    },
    {
      key: 'created_at',
      header: 'Date',
      render: (value: string) => (
        <div className="flex flex-col">
          <span>{new Date(value).toLocaleDateString()}</span>
          <span className="text-sm text-gray-500">
            {formatDistanceToNow(new Date(value), { addSuffix: true })}
          </span>
        </div>
      ),
    },
  ];

  const handleSearch = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    fetchComments(1, searchTerm);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-full dark:text-gray-200">Loading comments...</div>
    );
  }

  return (
    <div className="flex h-full bg-gray-50 dark:bg-gray-900">
      <div className="flex-1 flex flex-col min-w-0">
        {/* Header Section */}
        <div className="p-4 bg-white dark:bg-gray-800 shadow">
          <div className="mx-auto">
            <div className="flex flex-col sm:flex-row gap-4">
              <form onSubmit={handleSearch} className="flex-grow relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <FontAwesomeIcon icon={faSearch} className="text-gray-400 w-5 h-5" />
                </div>
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search comments..."
                  className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg bg-white dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200 placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </form>
            </div>
          </div>
        </div>

        {/* Table Section */}
        <div className="flex-grow overflow-hidden">
          <div className="h-full bg-white dark:bg-gray-800 shadow-sm">
            {comments?.length > 0 ? (
              <div className="h-full overflow-auto">
                <DataTable
                  data={comments}
                  columns={columns}
                  onRowClick={handleRowClick}
                  rowClassName="cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700"
                />
              </div>
            ) : (
              <div className="flex items-center justify-center h-full text-gray-500 dark:text-gray-400">
                No comments found
              </div>
            )}
          </div>
        </div>

        {/* Pagination Section */}
        {meta?.total_pages > 1 && (
          <div className="bg-white dark:bg-gray-800 border-t dark:border-gray-700 px-4 py-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <button
                  onClick={() => handlePageChange(meta.current_page - 1)}
                  disabled={meta.current_page === 1}
                  className="px-3 py-1 rounded transition-colors duration-150 disabled:opacity-50 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-600"
                >
                  Previous
                </button>
                <span className="text-gray-600 dark:text-gray-400">
                  Page {meta.current_page} of {meta.total_pages}
                </span>
                <button
                  onClick={() => handlePageChange(meta.current_page + 1)}
                  disabled={meta.current_page === meta.total_pages}
                  className="px-3 py-1 rounded transition-colors duration-150 disabled:opacity-50 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-600"
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AllComments;
